import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { URLService } from 'src/app/services/dynamic-url.service';

/** What GET /admin-dashboard/ai-usage returns to an institution admin (credits only). */
interface AiUsageLine {
  key: 'ai' | 'voice';
  label: string;
  credits: number;
  calls?: number;
  minutes?: number;
  bySpace?: { spaceId: string | null; name: string; credits: number; calls: number }[];
}
type CapKind = 'weekly' | 'monthly' | 'total';

/** Credit limits (pencil-api credit_caps.ts): current periods, independent of the month picked. */
interface AiUsageLimits {
  unlimited: boolean;
  caps: Record<CapKind, number | null>;
  used: Record<CapKind, number>;
  resetsAt: { weekly: string | null; monthly: string | null };
  capped: { kind: CapKind; used: number; cap: number; resetsAt: string | null } | null;
}

interface AiUsageReport {
  period: { from: string; to: string };
  totalCredits: number;
  lines: AiUsageLine[];
  daily: { date: string; ai: number; voice: number; credits: number }[];
  limits?: AiUsageLimits | null;
}

interface LimitRow {
  kind: CapKind;
  label: string;
  cap: number;
  used: number;
  fraction: number;
  resetsAt: string | null;
  /** The period has not started yet: it begins with the next agent use. */
  notStarted: boolean;
}

interface Month {
  from: Date;
  /** Start of the next month; absent for the current month (the API defaults to now). */
  to?: Date;
}

interface Day {
  date: string;
  ai: number;
  voice: number;
  total: number;
}

const MONTHS_SHOWN = 6;

/**
 * Settings → Usage: the institution's AI credits by month (AI_USAGE_ACCOUNTING_SPEC.md
 * phase 3). Credits only — never dollars, model names or tiers; voice is split by space.
 *
 * Only institution admins see the tab (the endpoint is isAdmin too). Months are
 * calendar months in UTC, the same boundaries the API uses.
 */
@UntilDestroy()
@Component({
  selector: 'app-user-settings-usage',
  templateUrl: './user-settings-usage.component.html',
  styleUrls: ['./user-settings-usage.component.scss'],
  // OnPush per project policy: every async callback calls markForCheck().
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsUsageComponent implements OnInit {
  @Input() isMobile = false;
  @Input() PANELS: any;
  // Shown as a tab of the AI coworker panel, which supplies the header.
  @Input() embedded = false;
  @Output() goBack = new EventEmitter<void>();

  months: Month[] = [];
  selected = 0;
  report: AiUsageReport | null = null;
  isLoading = false;
  failed = false;
  days: Day[] = [];
  maxDay = 0;

  private request?: Subscription;

  constructor(
    private readonly http: HttpClient,
    private readonly urlService: URLService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const now = new Date();
    for (let i = 0; i < MONTHS_SHOWN; i++) {
      const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
      const to = i === 0 ? undefined : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i + 1, 1));
      this.months.push({ from, to });
    }
    this.load();
  }

  selectMonth(index: number): void {
    if (index === this.selected || !this.months[index]) return;
    this.selected = index;
    this.load();
  }

  get ai(): AiUsageLine | undefined {
    return this.report?.lines.find((l) => l.key === 'ai');
  }

  get voice(): AiUsageLine | undefined {
    return this.report?.lines.find((l) => l.key === 'voice');
  }

  /** Limits come with every report; they describe the current periods. */
  limits: AiUsageLimits | null = null;

  get limitRows(): LimitRow[] {
    const l = this.limits;
    if (!l || l.unlimited) return [];
    const labels: Record<CapKind, string> = { weekly: 'Weekly', monthly: 'Monthly', total: 'Total' };
    return (['weekly', 'monthly', 'total'] as CapKind[])
      .filter((k) => l.caps[k] !== null)
      .map((k) => {
        const cap = l.caps[k] as number;
        const resetsAt = k === 'total' ? null : l.resetsAt[k];
        return {
          kind: k,
          label: labels[k],
          cap,
          used: l.used[k],
          fraction: cap > 0 ? Math.min(1, l.used[k] / cap) : 0,
          resetsAt,
          notStarted: k !== 'total' && !resetsAt,
        };
      });
  }

  get hasUsage(): boolean {
    return !!this.report && this.report.lines.some((l) => l.credits > 0 || (l.calls ?? 0) > 0);
  }

  /** Share of `part` in `whole`, as a CSS percentage. */
  share(part: number, whole: number): string {
    return whole > 0 ? `${Math.max(2, (part / whole) * 100)}%` : '0%';
  }

  barHeight(value: number): string {
    return this.maxDay > 0 ? `${(value / this.maxDay) * 100}%` : '0%';
  }

  private load(): void {
    const month = this.months[this.selected];
    const params: Record<string, string> = { from: month.from.toISOString() };
    if (month.to) params.to = month.to.toISOString();
    this.isLoading = true;
    this.failed = false;
    this.request?.unsubscribe();
    this.request = this.http
      .get<AiUsageReport>(`${this.urlService.getDynamicUrl()}/admin-dashboard/ai-usage`, { params })
      .pipe(
        catchError(() => of(null)),
        untilDestroyed(this),
      )
      .subscribe((report) => {
        this.isLoading = false;
        this.failed = !report;
        this.report = report;
        // Limits are current whatever month is shown; keep the last good answer.
        if (report?.limits !== undefined) this.limits = report.limits ?? null;
        this.days = report ? this.fillDays(report, month) : [];
        this.maxDay = Math.max(0, ...this.days.map((d) => d.total));
        this.cdr.markForCheck();
      });
  }

  /** Every day of the month up to today, zeros included, so the chart keeps its scale. */
  private fillDays(report: AiUsageReport, month: Month): Day[] {
    const byDate = new Map(report.daily.map((d) => [d.date, d]));
    const end = month.to ?? new Date();
    const days: Day[] = [];
    for (let t = month.from.getTime(); t < end.getTime(); t += 86_400_000) {
      const date = new Date(t).toISOString().slice(0, 10);
      const d = byDate.get(date);
      days.push({ date, ai: d?.ai ?? 0, voice: d?.voice ?? 0, total: d?.credits ?? 0 });
    }
    return days;
  }
}
